import { inject, injectable } from "@launchtray/tsyringe-async";
import { ModuleFlowExecutor } from "by/gamefactory/cityversetycoon/composition/module/ModuleFlowExecutor";
import { SceneManager } from "by/gamefactory/scene/SceneManager";
import { MyWalletModule } from "./MyWalletModule";
import { MyWalletPopupScene } from "./scenes/MyWalletPopupScene";
import { ILogger } from "by/gamefactory/cityversetycoon/logger/ILogger";
import { NoCacheDynamicAssets } from "../../common/resources/NoCacheDynamicAssets";
import { AsyncOperationNotification } from "../../common/components/AsyncOperationNotification";
import { TopPanel } from "../../game/components/toppanel/TopPanel";
import { IInventory } from "../../../../web3/models/IInventory";
import { WalletTabType } from "./components/WalletTabType";
import { MyWalletPopupSceneOptions } from "./scenes/MyWalletPopupSceneOptions";
import { Web3Model } from "../../../../web3/models/Web3Model";
import { IPlayerModel } from "../../common/models/IPlayerModel";
import { Web3Config } from "by/gamefactory/web3/config/Web3Config";

type MyWalletPopupSceneFactory = (options: MyWalletPopupSceneOptions) => Promise<MyWalletPopupScene>;

@injectable()
export class MyWalletModuleFlowExecutor extends ModuleFlowExecutor<MyWalletModule> {
    private static readonly NO_CACHE_BUILDINGS_TAG = "my_wallet_buildings";
    private static readonly NO_CACHE_PIGGY_CLUBS_TAG = "my_wallet_piggy_clubs";

    private readonly sceneManager: SceneManager;
    private readonly web3Model: Web3Model;
    private readonly web3Config: Web3Config;
    private readonly inventory: IInventory;
    private readonly logger: ILogger;
    private readonly buildingResources: NoCacheDynamicAssets;
    private readonly piggyClubResources: NoCacheDynamicAssets;
    private readonly asyncOperationNotification: AsyncOperationNotification;
    private readonly topPanel: TopPanel;
    private readonly playerModel: IPlayerModel;

    private topPanelOnNotificationAfterExecute: boolean;

    public constructor(
        sceneManager: SceneManager,
        web3Model: Web3Model,
        web3Config: Web3Config,
        @inject("IInventory") inventory: IInventory,
        asyncOperationNotification: AsyncOperationNotification,
        @inject("ILogger") logger: ILogger,
        @inject("IPlayerModel") playerModel: IPlayerModel,
        topPanel: TopPanel
    ) {
        super();
        this.sceneManager = sceneManager;
        this.web3Model = web3Model;
        this.web3Config = web3Config;
        this.inventory = inventory;
        this.asyncOperationNotification = asyncOperationNotification;
        this.logger = logger;
        this.topPanel = topPanel;
        this.playerModel = playerModel;
        this.buildingResources = new NoCacheDynamicAssets(MyWalletModuleFlowExecutor.NO_CACHE_BUILDINGS_TAG, true);
        this.piggyClubResources = new NoCacheDynamicAssets(MyWalletModuleFlowExecutor.NO_CACHE_PIGGY_CLUBS_TAG, true);
        this.topPanel.onLayerChanged.add(this.onTopPanelLayerChanged, this);
    }

    public override async execute(module: MyWalletModule, forceTab?: WalletTabType): Promise<void> {
        try {
            this.asyncOperationNotification.show();
            await this.web3Model.waitSmartWalletConnection();
            await Promise.all([
                this.web3Model.updateUSDCBalance(),
                this.web3Model.updateMONBalance(),
                this.web3Model.updateErc20Balance(this.web3Config.targetChain, this.web3Config.reservoirDevUsdcCurrency),
                this.web3Model.updateErc20Balance(this.web3Config.ethL1Chain, this.web3Config.monCurrencyAddress),
                this.inventory.update(),
                this.playerModel.loadBuildings(false)]);

            const inventoryBuildings = this.inventory.buildingsNFT;
            for (const inventoryBuilding of inventoryBuildings) {
                const buildingModel = this.playerModel.buildings.value.find((buildingModel) => buildingModel.id.value === inventoryBuilding.buildingId);
                if (buildingModel != null) {
                    this.buildingResources.add(inventoryBuilding.imageURL);
                    if (buildingModel.piggyClubImageUrl.value != null && buildingModel.piggyClubImageUrl.value != "") {
                        this.buildingResources.add(buildingModel.piggyClubImageUrl.value);
                    }
                }
            }
            await this.buildingResources.load();

            const inventoryPiggyClubs = this.inventory.piggyClubsNFT;
            for (const inventoryPiggyClub of inventoryPiggyClubs) {
                this.piggyClubResources.add(inventoryPiggyClub.imageURL);
            }
            await this.piggyClubResources.load();

            if (!forceTab) {
                if (this.inventory.hasBurnableDiceRolls) {
                    forceTab = WalletTabType.DICE_ROLLS;
                } else if (inventoryPiggyClubs.length > 0) {
                    forceTab = WalletTabType.PIGGY_CLUB;
                } else if (inventoryBuildings.length !== 0) {
                    forceTab = WalletTabType.BUILDING;
                } else {
                    forceTab = WalletTabType.PIGGY_CLUB;
                }
            }
            const myWalletFactory: MyWalletPopupSceneFactory = await module.resolve("MyWalletPopupSceneFactory");
            const scene = await myWalletFactory({ buildingResources: this.buildingResources, piggyClubResources: this.piggyClubResources, forceTab: forceTab });
            this.asyncOperationNotification.hide();
            this.topPanelOnNotificationAfterExecute = this.topPanel.onNotificationLayer;
            if (this.topPanelOnNotificationAfterExecute) {
                this.topPanel.bringToOriginParent(true);
            }
            await this.sceneManager.showViewableAsync(scene);
            this.topPanel.onLayerChanged.remove(this.onTopPanelLayerChanged, this);
            if (this.topPanelOnNotificationAfterExecute) {
                this.topPanel.bringToFront();
            }
            else {
                this.topPanel.bringToOriginParent();
            }
        }
        catch (e: unknown) {
            this.asyncOperationNotification.hide();
            this.logger.error(`Exception on executing MyWalletFlow: `, e);
        }
        finally {
            if (this.buildingResources.loaded) {
                this.buildingResources.destroy();
            }
            if (this.piggyClubResources.loaded) {
                this.piggyClubResources.destroy();
            }
            this.topPanel.onLayerChanged.remove(this.onTopPanelLayerChanged, this);
        }
    }

    private onTopPanelLayerChanged(): void {
        this.topPanelOnNotificationAfterExecute = this.topPanel.onNotificationLayer;
        if (this.topPanel.onNotificationLayer) {
            this.topPanel.bringToOriginParent(true);
        }
    }
}
